/**
 * Hook to manage a GQL mutation.
 *
 * For details on usage, see https://khanacademy.atlassian.net/wiki/spaces/ENG/pages/2015494150/useMutation.
 */
import * as React from "react";

import {useIsMounted} from "@khanacademy/wonder-blocks-core";
import {toGqlOperation, type Result} from "@khanacademy/wonder-blocks-data";
import {documentNode, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError, default as Log} from "@khan/logging";

import type {MutateFn, PartialGqlContext} from "./types.ts";
import {useGql} from "./use-gql.ts";
import {useRequestIdBuilder} from "./use-request-id-builder.ts";

/**
 * Describes the options that can be passed to the `useMutation` hook.
 */
type GqlMutationOptions = PartialGqlContext;

/**
 * Hook to manage a GQL mutation.
 *
 * A hook that provides the ability to invoke a specific mutation and to
 * track the result state.
 *
 * This can be used in two main ways:
 * 1. Using the returned function to invoke a mutation, and the returned result
 * to track that mutation and render UX accordingly.
 * 2. Using the returned function to invoke a mutation, and use its returned
 * promise to track the mutation and perform more complex activity on its
 * resolution.
 *
 * Both the returned result, and the mutation promise resolution have the same
 * result shape, with errors indicated by an error status rather than promise
 * rejection.
 *
 * NOTE: If a mutation invocation is successful but contains an error field,
 * it will be indicated as a successful invocation. Unpacking the error field
 * from the returned mutation data is the responsibility of the caller.
 *
 * @param {GqlOperation<TData, TVariables>} operation The GQL mutation
 * operation that will be invoked by the function returned from this hook.
 * @param {GqlMutationOptions} options Options for the hook. This can be used
 * to override values in the default GraphQL context for the page when
 * executing the mutation. These values can also be overridden at the point
 * of invoking the mutation. This can be useful if say you want all mutations to
 * operate on a different `module`, but each mutation call may also be on a
 * different `targetLocale`.
 * @returns {[
 *    (variables?: ?TVariables, context?: PartialContext) => Promise<Result<TData>>,
 *    ?Result<TData>,
 * ]} A tuple of the `mutate` function and the result state of the most recent
 * invocation. If there has been no invocation, this will be `null`.
 */
export const useMutation = <
    TData extends Record<any, any>,
    TVariables extends Record<any, any>,
>(
    operation: GqlOperation<TData, TVariables>,
    {curriculum, module, targetLocale}: GqlMutationOptions = Object.freeze({}),
): [MutateFn<TData, TVariables>, Result<TData> | null | undefined] => {
    if (process.env.NODE_ENV !== "production") {
        const wbdOperation = toGqlOperation(documentNode(operation));
        if (wbdOperation.type !== "mutation") {
            throw new KAError(
                "Queries are not valid for useMutation",
                Errors.InvalidInput,
            );
        }
    }

    // We use isMounted() to guard any state updates, since it's possible that
    // the mutate function is called after the component is unmounted.
    // (This is a use case we want to support sometimes - for example, saving
    // a user's progress when they navigate away from a page.)
    // In particular, we need to make sure we check isMounted() before we ever
    // call setResultOfMostRecentInvocation in order to avoid warnings about
    // updating an unmounted component.
    const isMounted = useIsMounted();

    // Use state to track the result state for the most recent mutate call.
    // Unlike queries, we don't share/track in-flight operations.
    // Instead, we only allow one in-progress mutation call at a time.
    const [resultOfMostRecentInvocation, setResultOfMostRecentInvocation] =
        React.useState<Result<TData> | null | undefined>(null);

    const requestIdBuilder = useRequestIdBuilder();

    // We track the in progress request using a ref.
    const inProgressRequestRef = React.useRef<
        | {
              id: string;
              result: Promise<Result<TData>>;
          }
        | null
        | undefined
    >(null);

    // Set up the mutate function.
    const gqlFetch = useGql();
    const mutateFn = React.useCallback(
        (variables, context) => {
            // Get the identifier for this request.
            const requestId = requestIdBuilder(operation, variables, {
                curriculum,
                module,
                targetLocale,
                ...context,
            });

            // If this is the same request we're already doing...
            if (
                inProgressRequestRef.current != null &&
                inProgressRequestRef.current.id === requestId
            ) {
                const {result} = inProgressRequestRef.current;
                // TODO(somewhatabstract): Consider allowing duplicate
                // mutations to go ahead instead. If we do that, we'll need
                // to use a timestamp or something to ensure that only the
                // most recent mutation actually affects the state, rather
                // than just the request ID.
                if (process.env.NODE_ENV !== "production") {
                    Log.log(
                        "useMutation: Returning in-progress request for duplicate mutation",
                        {
                            requestId,
                        },
                    );
                }
                // ...return the in-progress promise.
                return result;
            }

            // Indicate that we're busy.
            if (isMounted()) {
                setResultOfMostRecentInvocation({
                    status: "loading",
                });
            }

            // Perform the mutation.
            const doFetch = async (): Promise<Result<TData>> => {
                try {
                    const data = await gqlFetch(operation, {
                        variables: variables ?? undefined,
                        context: {
                            curriculum,
                            module,
                            targetLocale,
                            ...context,
                        },
                    });
                    // NOTE: A mutation could return "success" but then have
                    // an error with code set in the data. We currently treat
                    // this as success and have the downstream callers handle
                    // that. We could change this here to throw an error
                    // instead, but that would be a breaking change.
                    //
                    // Something like, if (data.error?.code) throw.
                    return {status: "success", data};
                } catch (error: any) {
                    return error.name === "AbortError"
                        ? {status: "aborted"}
                        : {
                              status: "error",
                              error,
                          };
                }
            };

            // NOTE: We don't support aborting inflight requests if a new
            // request comes in and folks want to cancel the old one
            // (see FEI-4276). Once a mutation is invoked, it's happening.
            // If the hook options change while a mutation is in-flight, this
            // will mean that the most recent result will be for the mutation
            // invoked with the previous options, though this is not likely
            // to occur often and should also not cause any issues. If we find
            // otherwise, we'll need to add some ref tracking and possibly
            // useEffect to make sure we properly isolate the in-flight
            // mutation from the result of the hook after those options were
            // modified.
            const resultPromise = doFetch().then((result) => {
                if (inProgressRequestRef.current?.id === requestId) {
                    // Clear the in-progress request.
                    inProgressRequestRef.current = null;
                    if (isMounted()) {
                        setResultOfMostRecentInvocation(result);
                    }
                }
                return result;
            });
            // Store the details of our in-progress request.
            inProgressRequestRef.current = {
                id: requestId,
                result: resultPromise,
            };
            return resultPromise;
        },
        [
            gqlFetch,
            operation,
            curriculum,
            module,
            targetLocale,
            requestIdBuilder,
            isMounted,
        ],
    );

    // Return the combo of the mutate function and the most recent result.
    // We memoize so that callers don't get a different tuple if nothing
    // changed, avoiding unnecessary updates when the result is used in a
    // dependency array of useEffect or similar.
    const tuple: [
        MutateFn<TVariables, TData>,
        Result<TData> | null | undefined,
    ] = React.useMemo(
        () => [mutateFn, resultOfMostRecentInvocation],
        [mutateFn, resultOfMostRecentInvocation],
    );
    return tuple;
};
